import { Component, Inject, OnInit } from '@angular/core';
import { switchMap, take, tap } from 'rxjs/operators';
import { UntypedFormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import {
  FieldConfigDTO,
  FormColumnConfigDTO,
  LookupEntityConf,
  MenuDTO,
  MenuType,
} from '../../../../../../system/system-dtos';
import { LookupService } from '../lookup.service';
import { SystemService } from '../../../../../../system/system.service';
import { PageResponseDTO } from '../../../../../../common/paging-dto';
import { MaintainService } from '../../../../../views/maintain/maintain.service';
import { StringUtils } from '../../../../../../common/string-utils';
import { EntityGroupDetailsComponent } from '../../../../../views/entity-group/entity-group-details/entity-group-details.component';
import { MenuUtils } from '../../../../../../common/utils/menu-utils';
import { EntityDetailsMode } from '../../../../../views/maintain/maintain-form/maintain-form-mode.enum';
import { notDefined } from '../../../../../utils/utils';
import { DataType } from '../../../data-type-component';

@Component({
  selector: 'app-lookup-modal',
  templateUrl: './lookup-modal.component.html',
  styleUrls: ['./lookup-modal.component.scss'],
  standalone: false,
})
export class LookupModalComponent implements OnInit {
  dataSource: MatTableDataSource<any>;

  menuConfig: MenuDTO;

  allFields: FieldConfigDTO[];

  filteredFields: string[];

  displayedFieldsNames: string[];

  loading = true;

  response: PageResponseDTO<any>;

  lookupConfig: LookupEntityConf;

  showAddForm: boolean = false;

  editFormConfig: FormColumnConfigDTO;

  editFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  missingIds: Set<string> = new Set<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LookupModalInitialData,
    public dialogRef: MatDialogRef<LookupModalComponent>,
    private lookupService: LookupService,
    private systemService: SystemService,
    private maintainService: MaintainService,
    private http: HttpClient
  ) {}

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnInit() {
    this.lookupConfig = this.lookupService.getConfByLookupCode(this.data.lookup);

    if (this.data.fieldConfig.lookupInsertUrl) {
      const editFields = this.data.fieldConfig.innerFields.filter((f: FieldConfigDTO) => !f.editHide);

      this.editFormConfig = {
        fields: editFields,
        fxFlex: '100',
        fullWidth: true,
      };

      editFields.forEach((f) => {
        const formControlForField = EntityGroupDetailsComponent.getFormControlForField(
          f,
          {},
          this.editFormConfig,
          EntityDetailsMode.VIEW
        );
        this.editFormGroup.addControl(f.name, formControlForField);
      });
    }

    this.systemService
      .getMenuConfig(this.data.lookup)
      .pipe(
        tap((menuConfig) => {
          this.menuConfig = menuConfig;
        })
      )
      .pipe(switchMap(() => this.getData(0, 20, this.getOrder())))
      .subscribe();
  }

  private getData(offset: number, perPage: number, order: string) {
    this.loading = true;
    return this.maintainService
      .selectAllData(StringUtils.convert(this.getSelectAllUrl(), this.data.context), { offset, perPage, order }, null)
      .pipe(tap((resp) => this.constructTableData(resp)))
      .pipe(tap(() => (this.loading = false)))
      .pipe(take(1));
  }

  private getSelectAllUrl() {
    if (this.lookupHasDefinedEndpoint()) {
      // commence hack, extract playerId from url
      const currentUrl: string = window.location.href;
      let playerId;
      if (currentUrl.includes('/players/')) {
        const parts: string[] = currentUrl.split('/');

        for (let x = 0; x < parts.length; x++) {
          if (parts[x] === 'players') {
            playerId = parts[x + 1];
          }
        }
      }
      if (playerId !== undefined) {
        return this.data.fieldConfig.selectUrl.replace('{id}', playerId);
      }
      return this.data.fieldConfig.selectUrl;
    }
    if (this.lookupConfig.defaultSelectUrl) {
      return this.lookupConfig.defaultSelectUrl;
    }

    return MenuUtils.getFirstMaintainConfig(this.menuConfig.entityGroup).tabs[0].endpoints.selectAllUrl;
  }

  private constructTableData(data: any) {
    this.response = this.lookupConfig.pageable
      ? data
      : {
          content: data,
          total: data.length,
          perPage: data.length,
          offset: 0,
        };
    this.allFields = this.constructMaintainFieldsArray();
    this.filteredFields = this.allFields.map((e) => e.name);
    this.displayedFieldsNames = this.lookupService
      .getConfByLookupCode(this.data.lookup)
      .tableFields.filter((el) =>
        this.filteredFields.includes(el)
      ); /* do not present column if no record contains it */
    this.dataSource = new MatTableDataSource(this.response.content);

    if (this.data.returnAll) {
      this.dataSource.filterPredicate = (data, filter) => {
        const dataJson = this.getValueString(data).toLowerCase();
        const splitted = filter.trim().split(/\s+/);
        return (
          splitted.filter((str) => {
            if (str[0] === '#') {
              return str.substr(1) === data.id || str.substr(1) === data.code;
            }
            return dataJson.includes(str);
          }).length > 0
        );

        /* let filterStr = '';
                const actualData = (data as any).data;
                Object.keys(actualData).forEach(k => {
                    const tickerData = actualData[k];
                    if (tickerData) {
                        filterStr += String(tickerData);
                        Object.keys(tickerData).forEach(k2 => filterStr += String(tickerData[k2]));
                    }
                });
                return filter.split(',').every(f => filterStr.toLowerCase().trim().includes(f.toLowerCase().trim())); */
      };
    }
  }

  private getValueString(data) {
    const dataJson = Object.keys(data)
      .map((key) => data[key])
      .reduce((acc, el) => acc + (el instanceof Object ? this.getValueString(el) : el), '');
    return dataJson;
  }

  private constructMaintainFieldsArray(): FieldConfigDTO[] {
    let rows = [];
    if (
      this.menuConfig &&
      MenuType.ENTITY_GROUP === this.menuConfig.menuType &&
      MenuUtils.getFirstMaintainConfig(this.menuConfig.entityGroup) !== null
    ) {
      rows = MenuUtils.getFirstMaintainConfig(this.menuConfig.entityGroup).rows;
    }

    if (rows.length === 0 || this.lookupHasDefinedEndpoint()) {
      const keys = Array.from(new Set(this.response.content.flatMap((r) => Object.keys(r))));

      return keys
        .filter((k) => this.lookupConfig.tableFields.find((tf) => tf === k))
        .map((k: string) => {
          const fieldType = this.findFieldType(k, this.lookupConfig);
          const fieldLabel = this.findFieldLabel(k, this.lookupConfig);
          return {
            name: k,
            type: fieldType,
            label: fieldLabel,
          };
        });
    }

    return rows
      .map((r) => r.columns)
      .reduce((acc, elem) => acc.concat(elem), [])
      .map((c) => c.fields)
      .reduce((acc, elem) => acc.concat(elem), []);
  }

  private lookupHasDefinedEndpoint() {
    return this.data.fieldConfig && this.data.fieldConfig.selectUrl;
  }

  findFieldType(name: string, entityConf: LookupEntityConf): DataType {
    const fieldType = this.findFieldConfigForName(entityConf, entityConf.fieldTypes, name);
    if (fieldType) {
      return DataType[fieldType];
    }
    return DataType.text;
  }

  findFieldLabel(name: string, entityConf: LookupEntityConf): string {
    const fieldLabel = this.findFieldConfigForName(entityConf, entityConf.fieldLabels, name);
    return fieldLabel || name;
  }

  findFieldConfigForName(entityConf: LookupEntityConf, fieldConfig: string[], name: string): string {
    let result = null;
    if (!fieldConfig || fieldConfig.length === 0) {
      return result;
    }
    if (
      entityConf.tableFields &&
      entityConf.tableFields.length > 0 &&
      entityConf.fieldTypes &&
      entityConf.fieldTypes.length > 0
    ) {
      for (let x = 0; x < entityConf.tableFields.length; x++) {
        if (entityConf.tableFields[x] === name) {
          if (fieldConfig.length > x) {
            result = fieldConfig[x];
          }
        }
      }
    }
    return result;
  }

  pageChanged(event: PageEvent) {
    this.getData(event.pageIndex, event.pageSize, this.getOrder()).subscribe();
  }

  onRowClick(row, event) {
    this.dialogRef.close(row);
  }

  onFilterChange($event: KeyboardEvent) {
    const text = ($event.target as HTMLTextAreaElement).value;
    this.dataSource.filter = text.trim().toLowerCase();
    if (this.data.returnAll) {
      const idsInFilter: Set<string> = new Set(
        text
          .split(/\s+/)
          .filter((s) => s.startsWith('#'))
          .map((s) => s.substr(1))
      );
      this.dataSource.filteredData.forEach((fd) => idsInFilter.delete(fd.id));
      this.dataSource.filteredData.forEach((fd) => idsInFilter.delete(fd.code));
      this.missingIds = idsInFilter;
    }
  }

  tryToAdd() {
    this.http.post(this.data.fieldConfig.lookupInsertUrl, this.editFormGroup.value).subscribe((d) => {
      this.dataSource.data.unshift(d);
      this.showAddForm = !this.showAddForm;
    });
  }

  returnAll() {
    this.dialogRef.close(this.dataSource.filteredData);
  }

  getOrder(): string {
    return this.lookupConfig.sortBy ? `${this.lookupConfig.sortBy},ASC` : null;
  }
}

export interface LookupModalInitialData {
  lookup: string;
  fieldConfig: FieldConfigDTO;
  context: any;
  returnAll?: boolean;
}
